import React, { useState } from 'react';
import { Badge } from '../ui/Badge';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';
import { EvidenceUpload } from './EvidenceUpload';
import { clsx } from 'clsx';

export const MilestoneTracker = ({ milestones: initialMilestones }) => {
  const [milestones, setMilestones] = useState(initialMilestones);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [expandedMilestone, setExpandedMilestone] = useState(null);

  const handleUpload = (milestoneIndex, evidence) => {
    setMilestones(prev => {
      const updated = [...prev];
      updated[milestoneIndex] = {
        ...updated[milestoneIndex],
        evidence: [...(updated[milestoneIndex].evidence || []), ...evidence]
      };
      return updated;
    });
    setUploadingFor(null);
  };

  const toggleExpand = (index) => {
    setExpandedMilestone(expandedMilestone === index ? null : index);
  };

  return (
    <div className="space-y-4">
      {milestones.map((m, idx) => (
        <div 
          key={idx} 
          className="bg-white border border-slate-200 rounded-lg hover:shadow-md transition-shadow overflow-hidden"
        >
          {/* Main Milestone Row */}
          <div className="flex items-start gap-4 p-4">
            <div 
              className={clsx(
                "mt-1 h-8 w-8 rounded-full flex items-center justify-center shrink-0",
                m.status === 'Completed' ? 'bg-green-100 text-green-600' :
                m.status === 'Overdue' ? 'bg-red-100 text-red-600' :
                'bg-blue-100 text-blue-600'
              )}
            >
              <SafeIcon 
                icon={
                  m.status === 'Completed' ? FiIcons.FiCheck :
                  m.status === 'Overdue' ? FiIcons.FiAlertTriangle :
                  FiIcons.FiClock
                } 
              />
            </div>
            
            <div className="flex-1">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-bold text-slate-800">{m.name}</h4>
                  <p className="text-xs text-slate-500">Due: {format(new Date(m.dueDate), 'dd MMM yyyy')}</p>
                </div>
                <Badge status={m.status} size="sm" />
              </div>

              {/* Evidence Count */}
              {m.evidence && m.evidence.length > 0 && (
                <button
                  onClick={() => toggleExpand(idx)}
                  className="flex items-center gap-2 text-xs text-grn-navy font-medium hover:underline mb-2"
                >
                  <SafeIcon icon={FiIcons.FiPaperclip} />
                  {m.evidence.length} evidence file(s) attached
                  <SafeIcon 
                    icon={expandedMilestone === idx ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} 
                  />
                </button>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => setUploadingFor(idx)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-grn-navy text-white rounded text-xs font-medium hover:bg-blue-900 transition-colors"
                >
                  <SafeIcon icon={FiIcons.FiUpload} />
                  Upload Evidence
                </button>
                {m.evidence && m.evidence.length > 0 && (
                  <button className="flex items-center gap-2 px-3 py-1.5 border border-slate-300 rounded text-xs font-medium hover:bg-slate-50 transition-colors">
                    <SafeIcon icon={FiIcons.FiDownload} />
                    Download All
                  </button>
                )}
              </div>
            </div>

            <button className="p-2 text-slate-400 hover:text-grn-navy hover:bg-slate-50 rounded">
              <SafeIcon icon={FiIcons.FiMoreVertical} />
            </button>
          </div>

          {/* Expanded Evidence List */}
          {expandedMilestone === idx && m.evidence && (
            <div className="px-4 pb-4 border-t border-slate-100 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {m.evidence.map((ev, evIdx) => (
                  <div 
                    key={evIdx}
                    className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg group hover:border-grn-navy/30 transition-all"
                  >
                    <div className="h-12 w-12 bg-white border border-slate-200 rounded overflow-hidden flex-shrink-0">
                      {ev.type?.startsWith('image/') ? (
                        <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                          <SafeIcon icon={FiIcons.FiImage} className="text-slate-400" />
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <SafeIcon 
                            icon={
                              ev.type === 'application/pdf' ? FiIcons.FiFileText :
                              FiIcons.FiFile
                            }
                            className="text-slate-400"
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-800 truncate">{ev.name}</p>
                      <p className="text-[10px] text-slate-500 font-mono mt-0.5">
                        SHA-256: {ev.hash.substring(0, 12)}...
                      </p>
                      {ev.metadata?.description && (
                        <p className="text-xs text-slate-600 mt-1 italic line-clamp-2">
                          "{ev.metadata.description}"
                        </p>
                      )}
                      <div className="flex gap-3 mt-2 text-[10px] text-slate-400">
                        {ev.uploadedAt && (
                          <span>{format(new Date(ev.uploadedAt), 'dd/MM/yy HH:mm')}</span>
                        )}
                        {ev.metadata?.uploadedBy && (
                          <span>• {ev.metadata.uploadedBy}</span>
                        )}
                      </div>
                    </div>
                    <button className="p-1.5 text-slate-400 hover:text-grn-navy opacity-0 group-hover:opacity-100 transition-opacity">
                      <SafeIcon icon={FiIcons.FiDownload} className="text-sm" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Upload Modal */}
      {uploadingFor !== null && (
        <EvidenceUpload
          milestoneId={milestones[uploadingFor].name}
          onUpload={(evidence) => handleUpload(uploadingFor, evidence)}
          onClose={() => setUploadingFor(null)}
        />
      )}
    </div>
  );
};